import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AnimationProvider, useAnimation } from './contexts/AnimationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { getTenantSubdomain, checkTenantExists } from './services/api';
import AuthLayout from './components/AuthLayout';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AdminDashboard from './components/AdminDashboard';
import AdminSettings from './components/AdminSettings';
import ActiveOperationsJsonPage from './components/ActiveOperationsJsonPage';
import ProfilePage from './components/ProfilePage';
import MyImputations from './components/MyImputations';
import EmployeeDashboard from './components/EmployeeDashboard';
import ManagerDashboard from './components/ManagerDashboard';
import TopBar from './components/TopBar';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import EmployeeProtectedRoute from './components/EmployeeProtectedRoute';
import ManagerProtectedRoute from './components/ManagerProtectedRoute';
import CreateImputationPage from './components/CreateImputationPage';
import EditImputationPage from './components/EditImputationPage';
import NoTenantPage from './components/NoTenantPage';
import InvalidTenantPage from './components/InvalidTenantPage';
import './App.css';
import './loader.css';

function LandingPage() {
  return (
    <iframe
      src="/landing_page.html"
      title="FollowUs Landing Page"
      style={{
        width: '100%',
        height: '100vh',
        border: 'none',
        display: 'block',
      }}
    />
  );
}

/** Platform marketing landing only; tenant hosts go straight to login. */
function RootPage() {
  const tenant = getTenantSubdomain();
  if (tenant) {
    return <Navigate to="/login" replace />;
  }
  return <LandingPage />;
}

/**
 * Blocks access when there is no tenant subdomain, or when the tenant does not exist in the backend DB.
 * - No tenant in URL -> NoTenantPage
 * - Tenant in URL but not in DB -> InvalidTenantPage
 * - Tenant in URL and exists in DB -> children (app routes)
 */
function TenantGuard({ children }) {
  const tenant = getTenantSubdomain();
  const [tenantValid, setTenantValid] = useState(null); // null = checking, true = exists, false = not found

  useEffect(() => {
    if (!tenant) {
      setTenantValid(null);
      return;
    }
    let cancelled = false;
    setTenantValid(null);
    checkTenantExists().then((exists) => {
      if (!cancelled) setTenantValid(exists);
    });
    return () => { cancelled = true; };
  }, [tenant]);

  if (!tenant) {
    return <NoTenantPage />;
  }
  if (tenantValid === null) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loader" style={{ margin: 'auto' }} />
      </div>
    );
  }
  if (tenantValid === false) {
    return <InvalidTenantPage />;
  }
  return children;
}

/** Stable key so employee section URL changes do not remount the dashboard shell. */
function getRouteAnimationKey(pathname) {
  const isEmployeeCreate =
    pathname === '/employee-dashboard/imputations/create';
  const isEmployeeEdit = pathname.startsWith('/employee-dashboard/imputations/edit/');
  if (
    pathname.startsWith('/employee-dashboard') &&
    !isEmployeeCreate &&
    !isEmployeeEdit
  ) {
    return '/employee-dashboard';
  }
  return pathname;
}

const employeeDashboardElement = (
  <EmployeeProtectedRoute>
    <EmployeeDashboard />
  </EmployeeProtectedRoute>
);

function RoutesWithAnimatePresence() {
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const isJsonOnlyRoute = location.pathname === '/active-operations-json';
  const routeAnimationKey = getRouteAnimationKey(location.pathname);

  return (
    <>
      {!isLanding && !isJsonOnlyRoute && <TopBar />}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={routeAnimationKey}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{ width: '100%', height: '100%' }}
        >
          <Routes location={location}>
            <Route path="/" element={<RootPage />} />
            <Route element={<AuthLayout />}>
              <Route path="login" element={<Login />} />
              <Route path="register" element={<Register />} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="reset-password" element={<ResetPassword />} />
            </Route>
            <Route 
              path="/admin-dashboard" 
              element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/employee-dashboard/imputations/create" 
              element={
                <EmployeeProtectedRoute>
                  <CreateImputationPage />
                </EmployeeProtectedRoute>
              } 
            />
            <Route 
              path="/employee-dashboard/imputations/edit/:id" 
              element={
                <EmployeeProtectedRoute>
                  <EditImputationPage />
                </EmployeeProtectedRoute>
              } 
            />
            <Route
              path="/employee-dashboard/planning/week/:weekStart"
              element={employeeDashboardElement}
            />
            <Route
              path="/employee-dashboard/planning/:calendarMode"
              element={employeeDashboardElement}
            />
            <Route
              path="/employee-dashboard/:section"
              element={employeeDashboardElement}
            />
            <Route
              path="/employee-dashboard"
              element={employeeDashboardElement}
            />
            <Route 
              path="/manager-dashboard" 
              element={
                <ManagerProtectedRoute>
                  <ManagerDashboard />
                </ManagerProtectedRoute>
              } 
            />
            <Route 
              path="/admin-settings" 
              element={
                <AdminProtectedRoute>
                  <AdminSettings />
                </AdminProtectedRoute>
              } 
            />
            <Route
              path="/active-operations-json"
              element={
                <ActiveOperationsJsonPage />
              }
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/my-imputations" 
              element={
                <ProtectedRoute>
                  <MyImputations />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </>
  );
}

function RoutesWithoutAnimatePresence() {
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const isJsonOnlyRoute = location.pathname === '/active-operations-json';

  return (
    <>
      {!isLanding && !isJsonOnlyRoute && <TopBar />}
      <Routes location={location}>
        <Route path="/" element={<RootPage />} />
        <Route element={<AuthLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
        </Route>
        <Route 
          path="/admin-dashboard" 
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="/employee-dashboard/imputations/create" 
          element={
            <EmployeeProtectedRoute>
              <CreateImputationPage />
            </EmployeeProtectedRoute>
          } 
        />
        <Route 
          path="/employee-dashboard/imputations/edit/:id" 
          element={
            <EmployeeProtectedRoute>
              <EditImputationPage />
            </EmployeeProtectedRoute>
          } 
        />
        <Route
          path="/employee-dashboard/planning/week/:weekStart"
          element={employeeDashboardElement}
        />
        <Route
          path="/employee-dashboard/planning/:calendarMode"
          element={employeeDashboardElement}
        />
        <Route
          path="/employee-dashboard/:section"
          element={employeeDashboardElement}
        />
        <Route
          path="/employee-dashboard"
          element={employeeDashboardElement}
        />
        <Route 
          path="/manager-dashboard" 
          element={
            <ManagerProtectedRoute>
              <ManagerDashboard />
            </ManagerProtectedRoute>
          } 
        />
        <Route 
          path="/admin-settings" 
          element={
            <AdminProtectedRoute>
              <AdminSettings />
            </AdminProtectedRoute>
          } 
        />
        <Route
          path="/active-operations-json"
          element={
            <ActiveOperationsJsonPage />
          }
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              {/* <Profile /> */}
              <ProfilePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/my-imputations" 
          element={
            <ProtectedRoute>
              <MyImputations />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </>
  );
}

function AppRoutes() {
  const { useRouteAnimatePresence } = useAnimation();

  return useRouteAnimatePresence ? (
    <RoutesWithAnimatePresence />
  ) : (
    <RoutesWithoutAnimatePresence />
  );
}

function App() {
  const location = useLocation();
  const isLanding = location.pathname === '/';

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AnimationProvider>
          {isLanding ? (
            <AppRoutes />
          ) : (
            <TenantGuard>
              <AppRoutes />
            </TenantGuard>
          )}
        </AnimationProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWithRouter;
